import React from "react";
import { createContext, useEffect, useRef, useState } from "react";
import { autorun } from "mobx";
import { io } from "socket.io-client";
import * as Sentry from "@sentry/react";
import config from "../config";
import PersonalAccount from "../store/PersonalAccount";

export const SOCKET_EVENTS = {
  JOIN_GAME: "join-room",
  /**
   * Событие обработчика свежих игровых данных от сокета 
   * приходят данные как с запроса current_user
   */
  REFRESH_GAME_DATA_MESSAGE: "refresh-game-data-message",
  /**
   * Событие запроса данных current_user с сервера, ответ придет в REFRESH_GAME_DATA_MESSAGE
   */ 
  REQUEST_REFRESH_GAME_DATA: "request-refresh-game-data",
  INSIGHT_MESSAGE: "insight-message",
  INSIGHT_EVENTS_DATA: "insight-events-data",
  PARTNER_BOUGHT_GAME: "partner-bought-game",
  SEND_INSIGHT_MESSAGE: "send-insight",
  /**
   * Перезапрос Insight форм
   */ 
  GET_BY_GAME: "get-by-game",
  /**
   * Событие для просмотра Insight форм и перевода из статус isViewed: false в true
   */ 
  ON_VIEW: "on-view",
  ZONE_CARD_CHECK_MESSAGE: "zone-card-check-message",

  /**
   * Событие принимает бэк, см. server/services/socket/events/game/end-game.js
   */ 
  END_GAME: "end-game",
  /**
   * 1. Отвечает за создание событий для показа уведомлений когда оба игрока раскидывают карточки
   * 2. Показывает модалку EndModal когда доступны результаты для обоих игроков
   */
  END_GAME_MESSAGE: "end-game-message",
  /**
   * Этап 2: начисление баллов лояльности (payload { userId, amount, balance })
   */
  POINTS_ACCRUED: "points-accrued",
  /**
   * Этап 2: партнёр разблокировал +57 карточек (payload { gameId, unlockedByUserId, partnerId })
   */
  EXTENDED_57_UNLOCKED: "extended-57-unlocked",
};
export const SOCKET_EVENT_TYPES = {
  STATUS: "status", 
  VIEWED: "viewed",
  INSIGHT_FORMS_DATA: "insight-forms-data",
  ALL_INSIGHT_EVENTS: "insight-message",
  ZONE_CARD_CHECK: "zone-card-check",
};
export const SocketContext = createContext(null);

export default function SocketProvider({ children }) {
  const socket = useRef(
    io(config.socketUrl, {
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 5000, // не меньше базовой задержки
      timeout: 20000,
    })
  );

  const [isConnected, setIsConnected] = useState(socket.current.connected);

  function sendEvent(event, value, ack) {
    if (ack) {
      socket.current.emit(event, value, ack);
      return;
    }
    socket.current.emit(event, value);
  }

  useEffect(() => {
    let timer;
    
    // Реактивно следим за пользователем и списком игр
    const disposer = autorun(() => {
      const user = PersonalAccount.user; // Читаем напрямую из MobX store
      const games = PersonalAccount.games || [];

      // Очищаем предыдущий таймер
      if (timer) {
        clearTimeout(timer);
      }

      if (!isConnected || !user || !games.length) {
        console.log('⚠️ Не присоединяемся к комнатам: ', {
          isConnected,
          userId: user?.id,
          gamesCount: games?.length
        });
        return;
      }

      timer = setTimeout(() => {
        console.log('🚪 Присоединяемся к комнатам для пользователя:', user.id);
        games.forEach((game) => {
          sendEvent(SOCKET_EVENTS.JOIN_GAME, {
            room: game.id,
            userId: user.id,
          });
          console.log(`🚪 User ${user.id} joined room: ${game.id}`);
        });
      }, 300);
    });

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      disposer();
      console.log('🧹 MobX autorun disposer called for room joining.');
    };
  }, [isConnected]); // Убрали getUserData из зависимостей

  useEffect(() => {
    console.log('🔌 SocketProvider: Инициализация соединения');
    
    // Счетчик неудачных попыток подключения
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS_BEFORE_ALERT = 5;
    
    socket.current.connect();

    function onDisconnect(reason) {
      console.log("❌ Socket disconnect:", reason);
      setIsConnected(false);
      
      // Отправляем в Sentry только критичные разрывы соединения
      if (reason === "io server disconnect" || reason === "io client disconnect") {
        // Сервер принудительно отключил или клиент явно вызвал disconnect
        Sentry.captureMessage(`Socket принудительно отключен: ${reason}`, {
          level: "warning",
          tags: {
            socket_event: "disconnect",
            disconnect_reason: reason,
          },
          contexts: {
            socket: {
              socketId: socket.current.id,
              connected: socket.current.connected,
            },
          },
        });
      }
    }
    
    function onConnect() {
      setIsConnected(true);
      console.log("✅ Socket connected, ID:", socket.current.id);
      
      // Сброс счетчика при успешном подключении
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS_BEFORE_ALERT) {
        Sentry.captureMessage(`Socket переподключился после ${reconnectAttempts} попыток`, {
          level: "info",
          tags: {
            socket_event: "reconnect_success",
          },
        });
      }
      reconnectAttempts = 0;
    }

    socket.current.on("connect", onConnect);
    socket.current.on("disconnect", onDisconnect);

    const onReconnect = (attemptNumber) => {
      console.log("🔄 Reconnected after", attemptNumber, "attempts");
      reconnectAttempts = 0;
    };
    
    const onReconnectAttempt = (attemptNumber) => {
      console.log("🔄 Reconnect attempt", attemptNumber);
      reconnectAttempts = attemptNumber;
      
      // Отправляем в Sentry после нескольких неудачных попыток
      if (attemptNumber === MAX_RECONNECT_ATTEMPTS_BEFORE_ALERT) {
        Sentry.captureMessage(`Socket: ${MAX_RECONNECT_ATTEMPTS_BEFORE_ALERT} неудачных попыток переподключения`, {
          level: "warning",
          tags: {
            socket_event: "reconnect_attempts",
            attempts: attemptNumber,
          },
          contexts: {
            socket: {
              socketId: socket.current.id,
              connected: socket.current.connected,
            },
          },
        });
      }
    };
    
    const onConnectError = (err) => {
      console.error("❌ Connect error:", err.message);
      
      // Отправляем в Sentry только после нескольких неудачных попыток
      // чтобы избежать спама из-за временных сбоев сети
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS_BEFORE_ALERT) {
        Sentry.captureException(err, {
          tags: {
            socket_event: "connect_error",
            attempts: reconnectAttempts,
          },
          contexts: {
            socket: {
              socketUrl: config.socketUrl,
              socketId: socket.current.id,
              connected: socket.current.connected,
            },
          },
        });
      }
    };
    
    const onReconnectFailed = () => {
      console.warn("⚠️ Reconnect failed");
      
      // Критичная ошибка - не удалось переподключиться совсем
      Sentry.captureMessage("Socket: переподключение полностью провалилось", {
        level: "error",
        tags: {
          socket_event: "reconnect_failed",
        },
        contexts: {
          socket: {
            socketUrl: config.socketUrl,
            socketId: socket.current.id,
            connected: socket.current.connected,
            reconnectAttempts: reconnectAttempts,
          },
        },
      });
    };

    socket.current.on("reconnect", onReconnect);
    socket.current.on("reconnect_attempt", onReconnectAttempt);
    socket.current.on("connect_error", onConnectError);
    socket.current.on("reconnect_failed", onReconnectFailed);
    
    return () => {
      console.log('🧹 SocketProvider: Cleanup - отключение сокета');
      socket.current.disconnect(); // важно для очистки
      socket.current.off("reconnect", onReconnect);
      socket.current.off("reconnect_attempt", onReconnectAttempt);
      socket.current.off("connect_error", onConnectError);
      socket.current.off("reconnect_failed", onReconnectFailed);

      socket.current.off("connect", onConnect);
      socket.current.off("disconnect", onDisconnect);
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket, isConnected, sendEvent }}>
      {children}
    </SocketContext.Provider>
  );
}
